import { containsPhrase, toSpacedWords } from './normaliseImportedService'
import {
  FUEL_SURCHARGE_TYPE,
  PERCENT_OF_CARRIAGE,
  SURCHARGE_TYPES,
  SURCHARGE_UNITS,
  Surcharge,
  SurchargeType,
  SurchargeUnit,
} from './types'

const NOON_CUT_OFFS = ['12', '12 pm', '12 00 pm', '12 noon', 'noon', 'midday']

const MORNING_PHRASES = [
  'am',
  'morning',
  ...['pre', 'before', 'by'].flatMap((cutOffWord) => NOON_CUT_OFFS.map((noon) => `${cutOffWord} ${noon}`)),
]

const AFTERNOON_PHRASES = ['pm', 'afternoon', 'after 12', 'after 12 pm', 'after 12 00 pm', 'after noon', 'after midday']

const PHRASES_THAT_NAME_EACH_SURCHARGE_TYPE: Record<SurchargeType, string[][]> = {
  [FUEL_SURCHARGE_TYPE]: [['fuel']],
  'Tail lift': [['tail lift', 'taillift']],
  'AM delivery': [MORNING_PHRASES],
  'PM delivery': [AFTERNOON_PHRASES],
  'Timed delivery': [['timed', 'time specific', 'specific time', 'time slot', 'timeslot', 'booked time']],
  'SAT AM delivery': [['saturday', 'sat'], MORNING_PHRASES],
}

const PHRASES_THAT_NAME_EACH_SURCHARGE_UNIT: Record<SurchargeUnit, string[]> = {
  'per consignment': ['consignment', 'consignments', 'con', 'cons', 'drop', 'delivery', 'shipment', 'flat'],
  'per pallet': ['pallet', 'pallets', 'plt', 'plts'],
}

const WAYS_TO_WRITE_A_PERCENTAGE = ['%', 'percent', 'percentage']

const FILLER_WORDS_IN_A_SURCHARGE_NAME = ['delivery', 'deliveries', 'del', 'charge', 'surcharge', 'fee', 'service', '00']

const FILLER_WORDS_IN_A_SURCHARGE_UNIT = ['per', 'fee', 'rate']

const byLongestPhraseFirst = (phrases: string[]): string[] => [...phrases].sort((a, b) => b.length - a.length)

const withoutPhrase = (words: string, phrase: string): string =>
  containsPhrase(words, phrase) ? withoutPhrase(words.replace(` ${phrase} `, ' '), phrase) : words

function isMadeOnlyOf(words: string, phraseGroups: string[][], fillerWords: string[]): boolean {
  const hasAPhraseFromEveryGroup = phraseGroups.every((phrases) =>
    phrases.some((phrase) => containsPhrase(words, phrase))
  )
  if (!hasAPhraseFromEveryGroup) return false
  const wordsLeftOver = byLongestPhraseFirst(phraseGroups.flat()).reduce(withoutPhrase, words)
  return wordsLeftOver
    .split(' ')
    .filter(Boolean)
    .every((word) => fillerWords.includes(word))
}

export function findSurchargeTypeNamedIn(name: string | undefined | null): SurchargeType | null {
  if (!name) return null
  const words = toSpacedWords(name)
  const surchargeType = SURCHARGE_TYPES.find((type) =>
    isMadeOnlyOf(words, PHRASES_THAT_NAME_EACH_SURCHARGE_TYPE[type.value], FILLER_WORDS_IN_A_SURCHARGE_NAME)
  )
  return surchargeType?.value ?? null
}

export function findSurchargeUnitNamedIn(unit: string | undefined | null): SurchargeUnit | null {
  if (!unit) return null
  const words = toSpacedWords(unit)
  const surchargeUnit = SURCHARGE_UNITS.find((supportedUnit) =>
    isMadeOnlyOf(words, [PHRASES_THAT_NAME_EACH_SURCHARGE_UNIT[supportedUnit.value]], FILLER_WORDS_IN_A_SURCHARGE_UNIT)
  )
  return surchargeUnit?.value ?? null
}

const isWrittenAsAPercentage = (unit: string | undefined): boolean =>
  !!unit && WAYS_TO_WRITE_A_PERCENTAGE.includes(unit.trim().toLowerCase())

const canFillSurchargeType = (surcharge: Surcharge, surchargeType: SurchargeType): boolean =>
  !surcharge.selectableSurchargeType &&
  findSurchargeTypeNamedIn(surcharge.name) === surchargeType &&
  (surchargeType !== FUEL_SURCHARGE_TYPE || isWrittenAsAPercentage(surcharge.unit))

const unitOfSurchargeType = (surchargeType: SurchargeType, writtenUnit: string | undefined): string | undefined =>
  surchargeType === FUEL_SURCHARGE_TYPE ? PERCENT_OF_CARRIAGE : findSurchargeUnitNamedIn(writtenUnit) ?? writtenUnit

const createBlankSurcharge = (type: (typeof SURCHARGE_TYPES)[number]): Surcharge => {
  const unit = unitOfSurchargeType(type.value, undefined)
  return {
    id: crypto.randomUUID(),
    name: type.label,
    price: null,
    ...(unit ? { unit } : {}),
    selectableSurchargeType: type.value,
  }
}

const giveSurchargeItsType = (surcharge: Surcharge, surchargeType: SurchargeType): Surcharge => ({
  ...surcharge,
  selectableSurchargeType: surchargeType,
  unit: unitOfSurchargeType(surchargeType, surcharge.unit),
})

const withoutSurchargeType = (surcharge: Surcharge): Surcharge => ({ ...surcharge, selectableSurchargeType: undefined })

export function fillEverySurchargeType(surcharges: Surcharge[]): Surcharge[] {
  const surchargesPlacedInATypeRow = new Set<Surcharge>()
  const rowForEachType = SURCHARGE_TYPES.map((type) => {
    const surchargeOfThisType =
      surcharges.find((surcharge) => surcharge.selectableSurchargeType === type.value) ??
      surcharges.find((surcharge) => canFillSurchargeType(surcharge, type.value))
    if (!surchargeOfThisType) return createBlankSurcharge(type)
    surchargesPlacedInATypeRow.add(surchargeOfThisType)
    return giveSurchargeItsType(surchargeOfThisType, type.value)
  })
  const surchargesToDelete = surcharges
    .filter((surcharge) => !surchargesPlacedInATypeRow.has(surcharge))
    .map(withoutSurchargeType)
  return [...rowForEachType, ...surchargesToDelete]
}
